import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Check } from "lucide-react";
import api, { unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { useAuth } from "@/context/AuthContext";
import UserAvatar from "@/components/UserAvatar";
import OnboardingShell from "@/pages/onboarding/OnboardingShell";
import { PrimaryButton, ErrorBox } from "@/components/AuthKit";

/**
 * SuggestedPeople — 1:1 port of iOS `src/app/(onboarding)/people.tsx`.
 * List of user rows with 48px avatars, name / @username / shared interests,
 * brand-purple "Follow"/"Following" pill button. Two-button footer:
 * "Skip" (secondary) + "Follow N & finish" or "Finish" (primary rainbow).
 */
export default function SuggestedPeople() {
  const navigate = useNavigate();
  const { refresh, triggerWelcome } = useAuth();

  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(EP.suggestedUsers(1, 20));
        const data = unwrap(res.data);
        const list = Array.isArray(data)
          ? data
          : data?.users || data?.items || data?.suggested || [];
        setPeople(list.map((u) => ({ ...u, id: u.user_id ?? u.id })));
      } catch (e) {
        setErr(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const complete = async (follow) => {
    setSaving(true);
    setErr("");
    try {
      if (follow && selected.size > 0) {
        await api.post(EP.followUsers, { user_ids: Array.from(selected) });
      }
      await api.post(EP.onboardingComplete, {});
      await refresh();
      triggerWelcome && triggerWelcome("Welcome to BrontoY");
      navigate("/home", { replace: true });
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingShell
      step="people"
      title="People you might like"
      subtitle="Follow a few to shape your feed. You can change this anytime."
      testId="onboarding-people-screen"
    >
      {loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-[76px] rounded-[18px] bg-secondary animate-pulse" />
          ))}
        </div>
      ) : people.length === 0 ? (
        <div
          className="rounded-3xl border border-border p-7 text-center"
          data-testid="onboard-people-empty"
        >
          <p className="text-muted-foreground text-[15px]">
            No suggestions yet. You can still continue.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {people.map((u) => {
            const active = selected.has(u.id);
            const name = u.display_name || u.name || u.username;
            return (
              <div
                key={String(u.id)}
                className="flex items-center gap-3 rounded-[18px] border border-border bg-card p-3.5"
                data-testid={`suggested-user-${u.id}`}
              >
                <UserAvatar user={u} size="lg" />
                <div className="flex-1 min-w-0">
                  <div className="text-foreground text-[15px] font-bold truncate">
                    {name}
                  </div>
                  <div className="text-muted-foreground text-[13px] truncate">
                    @{u.username}
                  </div>
                  {u.shared_interests?.length ? (
                    <div
                      className="text-muted-foreground text-[11px] font-bold uppercase mt-0.5 truncate"
                      style={{ letterSpacing: "1px" }}
                    >
                      {u.shared_interests.slice(0, 2).join(" · ")}
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => toggle(u.id)}
                  className={`h-9 px-3.5 rounded-full border-[0.5px] flex items-center gap-1.5 text-[13px] font-bold transition-all ${
                    active
                      ? "bg-[#7638FA] text-white border-[#7638FA]"
                      : "bg-transparent text-foreground border-border hover:bg-secondary"
                  }`}
                  data-testid={`suggested-follow-${u.id}`}
                >
                  {active ? (
                    <Check size={14} strokeWidth={2.5} />
                  ) : (
                    <Plus size={14} strokeWidth={2.5} />
                  )}
                  {active ? "Following" : "Follow"}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {err && (
        <div className="mt-4">
          <ErrorBox testId="onboard-people-error">{err}</ErrorBox>
        </div>
      )}

      <div className="flex gap-3 mt-6">
        <button
          type="button"
          onClick={() => complete(false)}
          disabled={saving}
          className="flex-1 h-[52px] rounded-2xl bg-secondary text-foreground text-[16px] font-bold border border-border hover:opacity-90 active:opacity-85 transition-all disabled:opacity-50"
          data-testid="onboard-people-skip"
        >
          Skip
        </button>
        <PrimaryButton
          label={selected.size > 0 ? `Follow ${selected.size} & finish` : "Finish"}
          onClick={() => complete(true)}
          loading={saving}
          className="flex-[1.6]"
          testId="onboard-people-finish"
        />
      </div>
    </OnboardingShell>
  );
}
